import { redirectToAuth } from './auth';
import type { BaseResponse, Meta } from './config';

export type QueryState<T> = {
  data: T | null;
  error: string | null;
  meta: Meta | null;
};

export type RequestOptions = {
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

export type ApiClient = {
  get<T>(path: string, options?: RequestOptions): Promise<QueryState<T>>;
  post<T, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: RequestOptions,
  ): Promise<QueryState<T>>;
  put<T, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: RequestOptions,
  ): Promise<QueryState<T>>;
  patch<T, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: RequestOptions,
  ): Promise<QueryState<T>>;
  delete<T>(path: string, options?: RequestOptions): Promise<QueryState<T>>;
};

function buildUrl(baseUrl: string, path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${baseUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

function buildHeaders(extra?: Record<string, string>): HeadersInit {
  return {
    'Content-Type': 'application/json',
    ...extra,
  };
}

async function parseResponse<T>(res: Response): Promise<QueryState<T>> {
  if (!res.ok) {
    if (res.status === 401) {
      redirectToAuth();
    }
    return { data: null, error: `HTTP ${res.status}: ${res.statusText}`, meta: null };
  }

  if (res.status === 204) {
    return { data: null, error: null, meta: null };
  }

  const contentLength = res.headers.get('content-length');
  const contentType = res.headers.get('content-type') ?? '';

  if (contentLength === '0' || !contentType.includes('application/json')) {
    return { data: null, error: null, meta: null };
  }

  const json: BaseResponse<T> = await res.json();
  return { data: json.data, error: null, meta: json.meta ?? null };
}

function handleError(err: unknown): QueryState<never> {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return { data: null, error: null, meta: null };
  }
  const message = err instanceof Error ? err.message : String(err);
  return { data: null, error: message, meta: null };
}

async function request<T>(
  baseUrl: string,
  method: string,
  path: string,
  options?: RequestOptions & { body?: unknown },
): Promise<QueryState<T>> {
  try {
    const res = await fetch(buildUrl(baseUrl, path), {
      method,
      headers: buildHeaders(options?.headers),
      credentials: 'include',
      body: options?.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options?.signal,
    });
    return parseResponse<T>(res);
  } catch (err) {
    return handleError(err);
  }
}

/**
 * Factory API client per package — base URL di-bind sekali.
 * Cookie HTTP-only via `credentials: 'include'`. Tidak perlu pass `base` di setiap call.
 *
 * @example
 * // packages/spotlight/src/api/client.ts
 * export const spotlightApi = createApiClient(import.meta.env.VITE_SPOTLIGHT_URL ?? '');
 *
 * // packages/spotlight/src/api/spotlight-api.ts
 * spotlightApi.get<SpotlightHistoryData>('/api/v1/search/history');
 * spotlightApi.post<SpotlightSearchData>('/api/v1/search', { query, limit: 10 });
 */
export function createApiClient(baseUrl: string): ApiClient {
  return {
    get<T>(path: string, options?: RequestOptions) {
      return request<T>(baseUrl, 'GET', path, options);
    },
    post<T, TBody = unknown>(path: string, body?: TBody, options?: RequestOptions) {
      return request<T>(baseUrl, 'POST', path, { ...options, body });
    },
    put<T, TBody = unknown>(path: string, body?: TBody, options?: RequestOptions) {
      return request<T>(baseUrl, 'PUT', path, { ...options, body });
    },
    patch<T, TBody = unknown>(path: string, body?: TBody, options?: RequestOptions) {
      return request<T>(baseUrl, 'PATCH', path, { ...options, body });
    },
    delete<T>(path: string, options?: RequestOptions) {
      return request<T>(baseUrl, 'DELETE', path, options);
    },
  };
}
